/**
 * Storage Migration
 *
 * One-time migration from legacy "icu-wissen" localStorage keys to "tricorder" keys.
 * This prevents users from losing auth state and preferences when upgrading.
 */

import {
  STORAGE_KEY_AUTH,
  STORAGE_KEY_CONFIG,
  STORAGE_KEY_RESPONSE_CACHE,
} from './constants';
import { createLogger } from '@/utils/logger';

const logger = createLogger('StorageMigration');

// Legacy storage keys (private to this module, used only for migration)
const LEGACY_STORAGE_KEY_CONFIG = 'icu-wissen-config' as const;
const LEGACY_STORAGE_KEY_AUTH = 'icu-wissen-auth' as const;
const LEGACY_STORAGE_KEY_RESPONSE_CACHE = 'icu-wissen-response-cache' as const;

/**
 * Migrate a single localStorage key from old name to new name.
 * If the new key already exists, skip migration (user already migrated).
 * After successful migration, delete the old key.
 */
function migrateKey(oldKey: string, newKey: string): void {
  try {
    // Skip if new key already exists
    if (localStorage.getItem(newKey) !== null) {
      logger.debug(`Migration skipped: ${newKey} already exists`);
      return;
    }

    // Check if old key exists
    const oldValue = localStorage.getItem(oldKey);
    if (oldValue === null) {
      logger.debug(`Migration skipped: ${oldKey} does not exist`);
      return;
    }

    // Migrate: copy to new key and delete old key
    localStorage.setItem(newKey, oldValue);
    localStorage.removeItem(oldKey);
    logger.info(`Migrated storage key: ${oldKey} → ${newKey}`);
  } catch (error) {
    logger.error(`Failed to migrate ${oldKey} → ${newKey}:`, error);
  }
}

/**
 * Erase a retired storage key. Unlike migrateKey, unconditional: some keys
 * held clinical content and must not survive the feature that read them.
 */
function eraseKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    logger.error(`Failed to erase ${key}:`, error);
  }
}

/**
 * Run all storage migrations.
 * Safe to call multiple times - migrations are idempotent.
 */
export function runStorageMigrations(): void {
  logger.debug('Running storage migrations');

  migrateKey(LEGACY_STORAGE_KEY_CONFIG, STORAGE_KEY_CONFIG);
  migrateKey(LEGACY_STORAGE_KEY_AUTH, STORAGE_KEY_AUTH);
  // The response cache stored clinical Q&A in localStorage; it is gone (Joël,
  // 14 October 2026: no PHI persists on the device) and both its keys are
  // erased, never migrated — devices still holding either one are wiped here.
  eraseKey(LEGACY_STORAGE_KEY_RESPONSE_CACHE);
  eraseKey(STORAGE_KEY_RESPONSE_CACHE);

  logger.debug('Storage migrations complete');
}
